import React, { ChangeEvent, useContext } from 'react'
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  withStyles,
  Theme,
  createStyles,
  WithStyles,
  Card,
  CardContent,
  Button
} from '@material-ui/core'
import {
  IPerson,
  IPersonFields,
  IPersonFieldsConfiguration
} from '../../models/Person'
import { OneplaceLibraryContext } from '../OneplaceLibraryProvider'
import PersonFields from './PersonFields'

const styles = (_theme: Theme) =>
  createStyles({
    root: {},
    label: { fontWeight: 'bold' },
    peopleLabel: { fontWeight: 'normal' },
    mandatoryField: { color: _theme.palette.secondary.main },
    card: {
      display: 'flex',
      padding: '0 24px 12px',
      margin: '12px 0'
    },
    details: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      flex: '1 1 auto'
    },
    content: {
      padding: '0 16px 24px 16px',
      flex: '1 0 auto'
    },
    trackedStyle: {
      borderLeft: '5px solid #ff5983'
    }
  })

export interface IPeoplePickerProps {
  selectedItems: IPerson[]
  [key: string]: any
}

export interface IPeopleTabProps extends WithStyles<typeof styles> {
  // the autocomplete component for picking people
  PeoplePicker: React.ComponentType<IPeoplePickerProps>

  selectedPeople: IPerson[]

  hardAssociation: boolean
  onHardAssociationChange: (e: any) => void
  hideHardAssociation?: boolean

  // optional person‐fields
  primaryPerson?: IPerson

  personFieldsConfig?: IPersonFieldsConfiguration
  personFields?: IPersonFields

  handleSelectPrimaryPerson?: (selectedPerson: IPerson | null) => Promise<void>

  disablePrimaryPersonSelect?: boolean

  hasTabs?: boolean

  nextTab?: () => void

  showRestricAccessCheckbox?: boolean
}

const PeopleTab = withStyles(styles)((props: IPeopleTabProps) => {
  const {
    classes,
    PeoplePicker,
    selectedPeople,
    primaryPerson,
    hardAssociation,
    onHardAssociationChange,
    hideHardAssociation,
    personFieldsConfig,
    personFields,
    disablePrimaryPersonSelect = false,
    hasTabs = false,
    nextTab,
    showRestricAccessCheckbox = true
  } = props

  const ctx = useContext(OneplaceLibraryContext)
  const t = ctx.i18next.t

  const onPrimaryPersonSelect = async (
    event: ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const selectedId = Number(event.target.value)
    const match = props.selectedPeople.find((p) => p.id === selectedId) ?? null

    if (match) await props.handleSelectPrimaryPerson?.(match)
  }

  return (
    <>
      <Card className={classes.card}>
        <div className={classes.details} data-testid='people-tab'>
          <CardContent className={classes.content}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <PeoplePicker selectedItems={selectedPeople} />
              </Grid>

              {selectedPeople.length > 0 &&
                personFieldsConfig?.anyFieldSelected && (
                  <Grid item xs={12}>
                    <FormControl variant='standard' fullWidth>
                      <InputLabel
                        htmlFor='primary-person-select'
                        className={classes.label}
                      >
                        {t('primary_person')}
                      </InputLabel>
                      <Select
                        id='primary-person-select'
                        value={primaryPerson?.id || -1}
                        onChange={async (e) => {
                          await onPrimaryPersonSelect(e)
                        }}
                        disabled={
                          disablePrimaryPersonSelect || !selectedPeople.length
                        }
                      >
                        {selectedPeople.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

              {personFieldsConfig?.anyFieldSelected &&
                selectedPeople.length > 0 &&
                primaryPerson && (
                  <Grid item xs={12}>
                    <PersonFields
                      configuration={personFieldsConfig}
                      primaryPerson={primaryPerson}
                      personFields={personFields}
                    />
                  </Grid>
                )}

              {showRestricAccessCheckbox && !hideHardAssociation &&
                (selectedPeople || []).some(
                  (person) => person.user || person.type === 'user'
                ) && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={hardAssociation}
                          onChange={onHardAssociationChange}
                          name='hardAssociation'
                          color='primary'
                        />
                      }
                      label={`Restrict access to Creator and User(s) selected in '${t(
                        'customLabel_people'
                      )}'`}
                    />
                  </Grid>
                )}
            </Grid>
          </CardContent>
        </div>
      </Card>
      {hasTabs && nextTab && (
        <div style={{ textAlign: 'right', marginBottom: 24 }}>
          <Button
            variant='contained'
            color='primary'
            style={{ marginRight: 12 }}
            onClick={nextTab}
          >
            {t('next')}
          </Button>
        </div>
      )}
    </>
  )
})

export default PeopleTab
